/* eslint-disable max-lines */

import { ClientLogger } from 'bernie-client';

import { adaptShortList } from 'stores/adapters/bex-api/migration-adapters/common/shortlist-adapter';
import { BadgeAdapter } from 'stores/adapters/bex-api/search/badge-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  BadgeModel,
  DataAttributes,
  GalleryViewModel,
  HotelFlags,
  HotelViewModel,
  ImageViewModel,
  ListingFooterViewModel,
  LodgingEnrichedMessageModel,
  PinnedHotelViewModel,
  RecentlyViewedModel,
  ReviewsViewModel,
  TravelAd,
} from 'models';
import {
  CommonPropertyCardFields,
  LodgingPwaPropertySearch,
  PropertyFields,
  PropertySearchTravelAds,
  StrikeOutType,
} from '__generated__/api/types';
import { ResultType } from 'constants/resultType';
import { ThemeName } from 'constants/theme';

const propertyType = 'HotelViewModel';
const logger = ClientLogger.getLoggerWithIdentifier('pwa.adapter.bex-api.search-results');

export const mapPropertySearchResults = (query: PropertySearchTravelAds.Query): HotelViewModel[] => {
  const properties = query.propertySearch.properties;

  if (!properties) {
    return null;
  }

  return (properties as (LodgingPwaPropertySearch.PropertyInlineFragment | PropertySearchTravelAds.Properties)[]).map(mapProperty);
};

export const getPropertiesFromResults = (query: PropertySearchTravelAds.Query): PropertySearchTravelAds.Properties[] =>
  query.propertySearch.properties;

export const mapSearchedDestination = (query: LodgingPwaPropertySearch.Query): string | null =>
  query.propertySearch.summary.region && query.propertySearch.summary.region.name;

export const mapProperty = (property?: LodgingPwaPropertySearch.PropertyInlineFragment): HotelViewModel => {
  if (!property || Object.keys(property).length === 0) {
    return {};
  }

  const id = getPropertyId(property);
  const priceModel = getPriceModel(property);
  const propertyFlags = getHotelFlags(property);

  return {
    amenities: property.amenities,
    dataAttributes: adaptDataAttributes(property),
    displayPrice: priceModel.lead,
    featuredMessages: adaptFeaturedMessages(property.featuredMessages),
    freeCancel: propertyFlags.freeCancel,
    gallery: getGalleryModel(property.imageGallery),
    hasAvailableOffer: propertyFlags.hasAvailableOffer,
    hotelFlags: propertyFlags,
    hotelId: id,
    hotelName: property.name,
    id,
    latitude: getCoordinate(property, 'latitude'),
    listingFooter: mapListingFooter(property),
    longitude: getCoordinate(property, 'longitude'),
    name: property.name,
    neighborhood: getLocationMessaging(property),
    newHotel: propertyFlags.newHotel,
    pinnedDetails: getPinnedHotelViewModel(property),
    price: priceModel,
    propertyDetailsLink: property.propertyDetailsLink,
    propertyListingBadgeModel: BadgeAdapter.getPropertyListingBadgeModel(property),
    propertySummary: property.highlightedPropertyDetails,
    propertySource: property.propertySource,
    recentlyViewed: getRecentlyViewed(property),
    resultType: ResultType.HOTEL,
    reviews: getReviewsModel(property.reviews),
    shortlistModel: adaptShortList(property.shortList),
    starRatingIcon: property.starRatingIcon,
    starRatingText: property.starRatingText,
    stars: getStarRating(property),
    supportingMessages: property.supportingMessages,
    thumbnail: getThumbnail(property),
    travelAd: getTravelAd(property),
    type: propertyType,
    vip: propertyFlags.isVip,
    vipMessage: property.vipMessaging,
  };
};

export const getGalleryModel = (gallery: PropertyFields.ImageGallery): GalleryViewModel => {
  if (!gallery) {
    return;
  }

  return {
    images: gallery.images.map(entry => ({
      caption: entry.image.description,
      alt: entry.image.description,
      src: entry.image.url,
      ...(entry.subjectId && { subjectId: entry.subjectId }),
      fallBackImage: entry.fallbackImage && entry.fallbackImage.url,
    })),
  };
};

// TODO: Change PWA model to string
const getPropertyId = (property: LodgingPwaPropertySearch.PropertyInlineFragment): number | null =>
  property.id ? Number.parseInt(property.id, 10) : null;

/**
 * Only return the star rating on a valid number
 */
export const getStarRating = (property: LodgingPwaPropertySearch.PropertyInlineFragment): number | null =>
  property.star && property.star > 0 ? property.star : null;

/**
 * Return distance from messaging if available, otherwise returns neighborhood
 */
export const getLocationMessaging = (property: LodgingPwaPropertySearch.PropertyInlineFragment): string | null => {
  if (property.destinationInfo && property.destinationInfo.distanceFromMessaging) {
    return property.destinationInfo.distanceFromMessaging;
  }

  return property.neighborhood ? property.neighborhood.name : null;
};

/**
 * Return coordinates, or null if there are no matching fields
 */
const getCoordinate = (property: LodgingPwaPropertySearch.PropertyInlineFragment, keyName: 'latitude' | 'longitude'): string | null => {
  const value = property.mapMarker && property.mapMarker.latLong && property.mapMarker.latLong[keyName];

  return value ? value.toString() : null;
};

/**
 * Always return a price model but the fields can be null
 */
export const getPriceModel = (property: LodgingPwaPropertySearch.PropertyInlineFragment) => {
  const price = property.price;

  return price?.displayMessages?.length
    ? price
    : {
        lead: price && price.lead && price.lead.formatted,
        strikeOut: price && price.strikeOut && price.strikeOut.formatted,
        strikeOutType: (price && price.strikeOutType) || StrikeOutType.INVALID,
        strikeoutDisclaimer: price && price.disclaimer && price.disclaimer.value,
        priceMessage: property.availability?.available
          ? null
          : {
              text: property.availability?.text,
              theme: property.availability?.theme && ThemeName[property.availability.theme],
            },
        numberOfNightsAndRooms: price && price.roomNightMessage,
        priceMessages: price && price.priceMessages,
        marketingFeeDetails: price && price.marketingFeeDetails,
        options: price && price.options,
      };
};

/**
 * Property reviews are non-null
 */
export const getReviewsModel = (reviews: CommonPropertyCardFields.Reviews): ReviewsViewModel | null => {
  if (!reviews) {
    return null;
  }

  const localizedOverallRating = reviews.localizedScore;
  const localizedCount = reviews.localizedTotal;
  const superlative = reviews.superlative;

  if (!localizedOverallRating && !localizedCount && !superlative) {
    return null;
  }

  return {
    overallRating: reviews.score,
    localizedOverallRating,
    count: reviews.total,
    localizedCount,
    superlative,
  };
};

/**
 * Property availability is non-null
 */
export const getHotelFlags = (property: LodgingPwaPropertySearch.PropertyInlineFragment): HotelFlags => ({
  hasAvailableOffer: property.availability?.available,
  isVip: Boolean(property.vipMessaging),
  newHotel: false,
});

/**
 * map and return recentlyViewedModel if it is non-null
 */
export const getRecentlyViewed = (property: LodgingPwaPropertySearch.PropertyInlineFragment): RecentlyViewedModel | null => {
  if (!property.recentlyViewed) {
    return null;
  }

  return {
    icon: property.recentlyViewed.id,
    message: property.recentlyViewed.description,
  };
};

/**
 * Return null if there is no image, instead of a null url
 */
export const getThumbnail = (property: LodgingPwaPropertySearch.PropertyInlineFragment): ImageViewModel | null => {
  if (!property) {
    return null;
  }

  const image: CommonPropertyCardFields.Image | null = property.propertyImage?.image;
  const fallbackImage: CommonPropertyCardFields.Image | null = property.propertyImage?.fallbackImage;

  if (!image && !fallbackImage) {
    return null;
  }

  return {
    caption: image?.description || fallbackImage?.description,
    thumbnailSrc: image?.url,
    src: image?.url,
    fallBackImage: fallbackImage?.url,
  };
};

/**
 * Always return a model, but the fields can be null if there is no offerBadge
 */
export const getPinnedHotelViewModel = (property: LodgingPwaPropertySearch.PropertyInlineFragment): PinnedHotelViewModel | null => {
  if (!property.pinnedDetails) {
    return null;
  }

  return property.pinnedDetails;
};

export const mapListingFooter = (property: LodgingPwaPropertySearch.PropertyInlineFragment): ListingFooterViewModel | null => {
  const listingFooter = property.listingFooter;

  if (!listingFooter) {
    return null;
  }

  return {
    icon: listingFooter.icon ? listingFooter.icon.id : null,
    actionModel: {
      text: listingFooter.text,
      rfrr: listingFooter.referrerId,
      href: listingFooter.linkUrl,
      actionDetails: listingFooter.actionDetails
        ? {
            name: listingFooter.actionDetails.name,
          }
        : null,
    },
  };
};

export const adaptDataAttributes = (property: LodgingPwaPropertySearch.PropertyInlineFragment): DataAttributes | null => {
  if (!property.dataAttributes) {
    return null;
  }
  const listing = {};
  listing[property.dataAttributes.key] = property.dataAttributes.value;

  return {
    listing,
  };
};

export const mapMessageModel = (
  message: LodgingPwaPropertySearch.MessageResultInlineFragment,
  __typeName
): LodgingPwaPropertySearch.MessageResultInlineFragment | null => {
  if (!message) {
    return null;
  }

  return { ...message, __typename: __typeName };
};

export const mapOmnitureData = (analytics: any): any | null => {
  if (Array.isArray(analytics) && analytics.length > 0 && analytics[0]) {
    return analytics[0];
  }
  logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_OMNITURE_PARSE_ERROR, 'Failed to parse omniture data');

  return null;
};

export const getTravelAd = (property: LodgingPwaPropertySearch.PropertyInlineFragment): TravelAd | null => {
  const sponsoredListing = property.sponsoredListing;
  if (sponsoredListing) {
    return {
      adId: sponsoredListing.id,
      clickRedirectTrackingUrl: sponsoredListing.clickRedirectTrackingUrl,
      clickTrackingUrl: sponsoredListing.clickTrackingUrl,
      details: sponsoredListing.details,
      detailsHeadline: sponsoredListing.detailsHeadline,
      hotelImage: sponsoredListing.hotelImage,
      impressionTrackingUrl: sponsoredListing.impressionTrackingUrl,
      carouselImpressionTrackingUrl: null,
      trackingData: sponsoredListing.trackingData,
      brand: sponsoredListing.brand,
      clickRedirectLinkoffTrackingUrl: sponsoredListing.clickRedirectLinkoffTrackingUrl,
      logo: sponsoredListing.logo,
      lotagline: sponsoredListing.lotagline,
      rating: sponsoredListing.rating,
      slots: sponsoredListing.slots,
      vanityurl: sponsoredListing.vanityUrl,
      rank: sponsoredListing.rank,
      position: sponsoredListing.position,
      candidateHmGuid: '',
      beaconIssued: '',
    };
  }

  return null;
};

export function adaptFeaturedMessages(featuredMessages: LodgingEnrichedMessageModel[]): BadgeModel[] {
  return featuredMessages?.map(message => ({
    icon: message.icon?.id,
    text: message.value,
  }));
}
